import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AudioLines, Check } from "lucide-react";
import { sfx } from "@/lib/sound";

const THEMES = [
  { id: "classic", name: "CLASSIC", desc: "Bamboo water garden" },
  { id: "modern", name: "MODERN", desc: "High-tech mechanical" },
  { id: "melody", name: "MUSIC BOX", desc: "Gentle showcase melody" },
];

export default function AudioMenu({ onOpenSoundTest }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(sfx.theme);
  const [vol, setVol] = useState(() => Math.round(sfx.volume * 100));
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (id) => {
    const wasOn = !!sfx.ambient;
    sfx.setTheme(id);
    setTheme(id);
    if (wasOn) {
      sfx.stopAmbient();
      sfx.startAmbient();
    }
    sfx.clank();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="audio-menu-btn"
        aria-label="Sound theme"
        onClick={() => {
          sfx.tick();
          setOpen((o) => !o);
        }}
        className={`iv-glass flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:text-[#0284C7] ${
          open ? "text-[#0284C7]" : "text-[#475569]"
        }`}
      >
        <AudioLines size={16} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="audio-menu-popover"
            className="iv-glass absolute right-0 top-12 z-50 w-60 rounded-xl p-2 shadow-xl"
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.18 }}
          >
            <div className="f-mono px-3 pt-2 pb-1.5 text-[8px] tracking-[0.35em] text-[#94A3B8]">
              MASTER VOLUME · {vol}%
            </div>
            <div className="px-3 pb-2">
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={vol}
                data-testid="volume-slider"
                aria-label="Master volume"
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setVol(v);
                  sfx.setVolume(v / 100);
                }}
                onPointerUp={() => sfx.clank()}
                className="iv-volume-slider w-full"
              />
            </div>
            <div className="f-mono px-3 pt-1 pb-1.5 text-[8px] tracking-[0.35em] text-[#94A3B8]">
              SOUND THEME
            </div>
            {THEMES.map((th) => (
              <button
                key={th.id}
                type="button"
                data-testid={`theme-${th.id}-btn`}
                onClick={() => pick(th.id)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/60 ${
                  theme === th.id ? "bg-white/50" : ""
                }`}
              >
                <span>
                  <span
                    className={`f-mono block text-[10px] tracking-[0.25em] ${
                      theme === th.id ? "text-[#0284C7]" : "text-[#0F172A]"
                    }`}
                  >
                    {th.name}
                  </span>
                  <span className="text-[10px] text-[#94A3B8]">{th.desc}</span>
                </span>
                {theme === th.id && <Check size={13} className="shrink-0 text-[#0284C7]" />}
              </button>
            ))}
            <div className="mt-1 border-t border-white/60 pt-1">
              <button
                type="button"
                data-testid="sound-test-open-btn"
                onClick={() => {
                  setOpen(false);
                  onOpenSoundTest();
                }}
                className="f-mono w-full rounded-lg px-3 py-2.5 text-left text-[9px] tracking-[0.25em] text-[#475569] transition-colors hover:bg-white/60 hover:text-[#0284C7]"
              >
                SOUND TEST ROOM ▸
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
